// In-browser LLM helpers — the opt-in "offline mode" surface for the Playground
// practices (wiki/product-vision.md §"The seven practices" #1, #3, #4). Neither
// branch asks for JSON or inline cue tags: a 1B-3B model can't reliably weave
// either, so read-aloud returns plain prose and Riff returns a word plus
// period-terminated sentences; the backend cue endpoint owns the cue plan.
// `@mlc-ai/web-llm` is imported dynamically so offline-mode-OFF pays no bundle
// cost, and WebGPU detection never imports it at all.
import type { PracticeContentEnvelope } from "$lib/types/practiceContent";
import {
    OutputTokensPerWord,
    Practice,
    PromptsOfflineKeys,
    RIFF_WORD_CATEGORIES,
    UiKeys,
} from "@voicedojo/backend-types";
import { t } from "$lib/i18n";
import type { PracticeSlug } from "$lib/practice/practiceSlug";
import {
    DEFAULT_PRACTICE_LENGTH_MINUTES,
    targetWordsFor,
    wordsPerMinute,
} from "$lib/utils/content/practiceLength";
import { randomFallbackTitle } from "$lib/utils/formatting/playfulTitlePlaceholders";
import {
    loadModelCatalog,
    webLlmAppConfig,
    type CatalogModel,
} from "$lib/modelMirror/modelMirror";

// The offered models come from the mirror's catalog (modelMirror.ts), authored
// in pinned-models.json. Tiers bucket them by GPU memory; the picker hides tiers the GPU
// cannot run (getAvailableTiers).
export type Tier = "tiny" | "small" | "medium" | "large";

export const DEFAULT_MODEL_ID = "Llama-3.2-1B-Instruct-q4f16_1-MLC";

// An id the catalog no longer offers (a stale localStorage value) resolves to
// the default, then to the first recommended model, never to a dead load.
export function pickModel(
    models: readonly CatalogModel[],
    modelId: string,
): CatalogModel {
    const model =
        models.find((m) => m.id === modelId) ??
        models.find((m) => m.id === DEFAULT_MODEL_ID) ??
        models.find((m) => m.recommended);
    if (!model) throw new Error("The model catalog offers no model.");
    return model;
}

// f16 builds need WebGPU `shader-f16`, which Chrome's Vulkan (Linux) backend
// lacks; f32 is the mandatory WGSL type and runs everywhere.
export const resolveModelId = (model: CatalogModel, shaderF16: boolean): string =>
    (shaderF16 ? model.f16 : model.f32).model_id;

export const downloadSizeMb = (model: CatalogModel, shaderF16: boolean): number =>
    model.downloadMb[shaderF16 ? "f16" : "f32"];

// Unchecked "Show all" collapses the picker to each tier's recommended model.
export function filterModelsForPicker(
    models: readonly CatalogModel[],
    showAll: boolean,
): readonly CatalogModel[] {
    if (showAll) return models;
    return models.filter((m) => m.recommended);
}

/**
 * Approximate VRAM floor in MiB. We don't get a real VRAM number from
 * `navigator.gpu` — the adapter exposes only abstract buffer-size limits.
 * `maxBufferSize` and `maxStorageBufferBindingSize` are usually reported as
 * 1GiB or 2GiB on a working GPU and clamp to ~256MiB on weak / virtualised
 * adapters (the same ones that would fail to load a 700MB model). 1GiB is
 * the line: anything above probably runs; anything below is unsafe.
 */
const MIN_BUFFER_SIZE_BYTES = 1024 * 1024 * 1024;

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/**
 * Coarse classification of the WebGPU adapter the browser handed us. Drives
 * the GPU-aware tier filter in {@link getAvailableTiers} together with — but
 * NOT solely on — `estimatedVramMb`. See {@link detectWebGpu} for why we need
 * BOTH signals.
 *
 *  - `'discrete'`       — a discrete GPU from NVIDIA / AMD / etc. These can
 *                         always run Medium and almost always run Large
 *                         consumer-class models regardless of what
 *                         `maxBufferSize` reports.
 *  - `'apple-silicon'`  — Apple M-series unified-memory GPU. Lots of VRAM
 *                         via unified memory; treated like discrete for
 *                         tier purposes (M1+ comfortably runs 7-8B models).
 *  - `'integrated'`     — typical iGPU (Intel UHD/Iris/Xe, AMD APU graphics).
 *                         Tier capped by `estimatedVramMb` alone.
 *  - `'unknown'`        — adapter exposes no usable info (old browser / fresh
 *                         API surface). Fall back to `estimatedVramMb` for
 *                         the tier filter; we don't assume capability we
 *                         can't verify.
 */
export type GpuClass = "discrete" | "apple-silicon" | "integrated" | "unknown";

/**
 * Result of {@link detectWebGpu}. `supported=true` means the toggle should
 * be exposed; `false` hides it. `reason` is for diagnostic display only.
 *
 * `estimatedVramMb` is the larger of the two buffer-size limits (in MiB)
 * the adapter reports — a *floor* proxy, NOT real VRAM. Chrome and Edge
 * cap `maxBufferSize` at ~2 GiB regardless of the physical VRAM the GPU
 * actually has (a 32 GB RTX 5090 still reports ~2048 MiB here). Treat this
 * field as "the smallest plausible upper bound" — useful for ruling out
 * the toggle entirely (<1 GiB ⇒ definitely too weak) but useless for
 * deciding Medium vs Large on a real discrete GPU.
 *
 * `gpuClass` is the orthogonal signal that lets us promote a real discrete
 * GPU to higher tiers even though its `maxBufferSize` is capped. Derived
 * from `adapter.info.vendor` + `adapter.info.architecture`. Always present
 * when `supported=true`; defaults to `'unknown'` when adapter info is
 * unavailable (older browsers without `GPUAdapter.info`).
 */
export type WebGpuDetection = {
    supported: boolean;
    reason?: string;
    estimatedVramMb?: number;
    gpuClass?: GpuClass;
    /**
     * Whether the adapter exposes the WebGPU `shader-f16` extension. f16 models
     * (`q4f16_1` / `q0f16`) need it; without it (Chrome on Vulkan/Linux) we load
     * the f32 sibling — see {@link resolveModelId}. `false` when the adapter
     * lacks the feature OR when there is no adapter (unsupported). f32 needs no
     * probe — it is the mandatory WGSL type, so "f32 available" ≡ `supported`.
     */
    shaderF16: boolean;
};

/**
 * `download` while the model's files come from the network; `load` while they
 * are read from Cache Storage and the model is compiled and initialised on the
 * GPU. A load starts in `download` unless the weights are known to be cached,
 * and never returns to it.
 */
export type ModelLoadPhase = "download" | "load";

/**
 * `progress` is 0..1 and `text` is the MLC pipeline's status line (e.g.
 * "Loading model from cache[12/24]" or "Fetching param cache[3/24]").
 */
export type ModelLoadProgress = {
    progress: number;
    text: string;
    phase: ModelLoadPhase;
};

// web-llm 0.2.87 reports no structured phase. Its weight fetch is the only step
// whose reports read "Start to fetch params" and "Fetching param cache[…]"; the
// config, tokenizer and model library are fetched before any report, and every
// report after the fetch reads from Cache Storage or initialises the GPU.
const FETCH_REPORT = /^(Start to fetch params|Fetching param cache)/;

export function nextLoadPhase(
    current: ModelLoadPhase,
    text: string,
): ModelLoadPhase {
    return current === "download" && FETCH_REPORT.test(text)
        ? "download"
        : "load";
}

/**
 * The kind of practice content to generate. Distinct from the backend
 * `Practice` enum: a handful of practices are offline-eligible (the cue
 * vocabulary is static and there is no AI Review), and the prompt scaffold
 * is tailored per-practice.
 *
 * `'riff'` is a special case — its content is a single English word plus a
 * pre-generated list of "I need help!" prompts, not a story to read aloud.
 * The generator branches on the practice: Riff asks for plain text (the word
 * first, then period-terminated help sentences) and splits it on periods,
 * while the read-aloud practices stream prose straight into the body.
 */
export type OfflinePractice = PracticeSlug;

/**
 * Opaque handle for the loaded engine. We type it as `unknown`-ish so the
 * heavy web-llm types don't leak through to consumers — the only public
 * operation is "pass it back to `generatePracticeContent`".
 */
export type InBrowserEngine = {
    readonly modelId: string;
    // Internal: the web-llm MLCEngine. Hidden behind a brand so call sites
    // can't reach inside the engine and depend on web-llm types.
    readonly _engine: unknown;
};

// ---------------------------------------------------------------------------
// detectWebGpu
// ---------------------------------------------------------------------------

/**
 * Detects whether the current browser environment can run an in-browser LLM
 * via WebGPU. The check is conservative — we'd rather hide the toggle for a
 * borderline-capable adapter than offer it and have model load fail with a
 * cryptic device-lost error.
 *
 * Returns `{ supported: false, reason }` when:
 *   - There is no `navigator.gpu` (older browsers, Firefox stable < 141, etc.)
 *   - `requestAdapter()` resolves to `null` (no GPU available — typically
 *     headless environments or VMs without graphics passthrough).
 *   - The adapter reports buffer-size limits below {@link MIN_BUFFER_SIZE_BYTES}.
 */
export async function detectWebGpu(): Promise<WebGpuDetection> {
    // SSR safety — `navigator` is undefined under Node.
    if (typeof navigator === "undefined") {
        return {
            supported: false,
            reason: "No navigator (server-side render)",
            shaderF16: false,
        };
    }

    // `navigator.gpu` is the WebGPU root. Missing ⇒ unsupported browser.
    const gpu = (navigator as Navigator & { gpu?: GPU }).gpu;
    if (!gpu) {
        return {
            supported: false,
            reason: "WebGPU is not available in this browser. Try a recent Chrome, Edge, or Firefox 141+.",
            shaderF16: false,
        };
    }

    let adapter: GPUAdapter | null;
    try {
        adapter = await gpu.requestAdapter();
    } catch (err) {
        return {
            supported: false,
            reason: `WebGPU adapter request failed: ${(err as Error).message ?? "unknown error"}`,
            shaderF16: false,
        };
    }

    if (!adapter) {
        return {
            supported: false,
            reason: "No WebGPU adapter — the browser exposes WebGPU but cannot find a usable GPU.",
            shaderF16: false,
        };
    }

    // The two limit fields that proxy "can we hold a 700MB model and its KV
    // cache". `maxStorageBufferBindingSize` is the size of any single bound
    // buffer (the model weights live across several of these); `maxBufferSize`
    // is the overall ceiling. Pick the larger as an optimistic floor probe.
    //
    // IMPORTANT — this is NOT a real VRAM number. Chrome and Edge clamp
    // `maxBufferSize` at ~2 GiB regardless of the physical VRAM the GPU has,
    // so a 32 GB RTX 5090 reports the same ~2048 MiB here as a 2 GB GTX 1050.
    // We use this field only as a *floor* check ("can the adapter even hold a
    // 1 GiB buffer?") and rely on `gpuClass` (read from adapter.info below)
    // for Medium / Large promotion. Without that second signal the 4 GiB /
    // 8 GiB thresholds in {@link getAvailableTiers} would never trigger and
    // every visitor — even on a flagship discrete GPU — would only see
    // Tiny + Small. (This was the original bug: a 32 GB GPU stuck at Small.)
    const limits = adapter.limits;
    const maxBinding = Number(limits.maxStorageBufferBindingSize ?? 0);
    const maxBuffer = Number(limits.maxBufferSize ?? 0);
    const probeBytes = Math.max(maxBinding, maxBuffer);
    const estimatedVramMb = Math.round(probeBytes / (1024 * 1024));

    if (probeBytes < MIN_BUFFER_SIZE_BYTES) {
        return {
            supported: false,
            reason: `WebGPU buffer limit too small (${estimatedVramMb}MiB) — need at least 1GiB.`,
            estimatedVramMb,
            shaderF16: false,
        };
    }

    const gpuClass = await deriveGpuClass(adapter);

    // `shader-f16` extension probe. f16 models (`q4f16_1` / `q0f16`) require it;
    // Chrome exposes it on D3D12 (Windows) and Metal (macOS) but NOT on Vulkan
    // (Linux), so without it the all-f16 list fails to load. The adapter is
    // already in hand. `adapter.features` is a GPUSupportedFeatures (Set-like
    // with `.has()`); guard defensively in case a stub/older adapter omits it.
    const shaderF16 = hasFeature(adapter, "shader-f16");

    return { supported: true, estimatedVramMb, gpuClass, shaderF16 };
}

/**
 * Reads `adapter.features.has(name)` defensively. Returns `false` when the
 * adapter (or a test stub) exposes no `features` set, or when `.has` throws.
 */
function hasFeature(
    adapter: { features?: { has?: (name: string) => boolean } },
    name: string,
): boolean {
    try {
        return adapter.features?.has?.(name) === true;
    } catch {
        return false;
    }
}

/**
 * Reads the WebGPU adapter's `info` (vendor + architecture strings) and
 * classifies it into {@link GpuClass}. Used by {@link detectWebGpu} as the
 * second signal — `estimatedVramMb` is capped at ~2 GiB by the browser even
 * on a 32 GB discrete GPU, so we need a *different* fingerprint to decide
 * Medium vs Large.
 *
 * The classification is deliberately vendor-based, not GPU-model-based:
 *   - NVIDIA / AMD vendors are virtually always discrete in a desktop
 *     browser (AMD APUs report as "amd" too, but the buffer-size check
 *     still gates those at the Tiny+Small floor — we don't drop tiers).
 *   - Apple "apple-m..." architectures (or vendor "apple" + an architecture
 *     starting with "apple-m") cover the M-series unified-memory designs,
 *     which run 7-8B models comfortably.
 *   - Intel / "alder-lake" / iGPU architectures classify as integrated.
 *   - Anything else (or info-less adapters) classifies as `'unknown'`,
 *     and {@link getAvailableTiers} falls back to the `estimatedVramMb`
 *     thresholds for those.
 *
 * Future-proofing: this function does NOT hardcode any specific GPU model
 * (no "RTX 5090" string). Adding support for a new NVIDIA architecture
 * (Blackwell, Rubin, …) requires zero changes here — the vendor `"nvidia"`
 * already classifies the whole line as discrete.
 *
 * Exported for unit testing.
 */
export async function deriveGpuClass(adapter: {
    info?: GpuAdapterInfoLike;
    requestAdapterInfo?: () => Promise<GpuAdapterInfoLike>;
}): Promise<GpuClass> {
    // `GPUAdapter.info` is the sync field (Chrome 134+). The
    // `requestAdapterInfo()` async method works on older Chrome and is still
    // present on the WebGPU spec. Try the sync field first; fall back
    // to the async method; tolerate either being absent or throwing.
    let info: GpuAdapterInfoLike | undefined;
    try {
        info = adapter.info ?? (await adapter.requestAdapterInfo?.());
    } catch {
        info = undefined;
    }
    if (!info) return "unknown";

    const vendor = (info.vendor ?? "").toString().trim().toLowerCase();
    const architecture = (info.architecture ?? "")
        .toString()
        .trim()
        .toLowerCase();
    const device = (info.device ?? "").toString().trim().toLowerCase();
    const description = (info.description ?? "")
        .toString()
        .trim()
        .toLowerCase();
    const haystack = `${vendor} ${architecture} ${device} ${description}`;

    // Apple Silicon — unified-memory designs comfortably run 7B+ models. The
    // architecture string is "apple-m1" / "apple-m2" / "apple-m3" / etc.
    // (future M-series tags will match "apple-m..."). Apple ALSO ships the
    // AGX architecture on iOS — that's classed integrated, but iOS Safari
    // doesn't yet ship WebGPU broadly, so this stays a non-issue.
    if (
        architecture.startsWith("apple-m") ||
        /\bapple m[0-9]+/.test(haystack)
    ) {
        return "apple-silicon";
    }

    // Discrete vendors. AMD APUs report vendor "amd" too — but the 1 GiB
    // buffer floor in detectWebGpu already filters out the weakest of those,
    // and the few APUs that pass the floor genuinely CAN run Medium models.
    // NVIDIA on a consumer browser is always discrete.
    if (
        vendor === "nvidia" ||
        vendor.includes("nvidia") ||
        /\bnvidia\b/.test(haystack)
    ) {
        return "discrete";
    }
    if (
        vendor === "amd" ||
        vendor === "ati" ||
        /\b(?:radeon|rdna|amd)\b/.test(haystack)
    ) {
        return "discrete";
    }

    // Integrated — Intel iGPUs (UHD / Iris / Xe), Adreno (Android Qualcomm),
    // Mali (Arm), and anything that explicitly identifies as integrated. We
    // pattern-match the architecture / device strings since Chrome reports
    // these inconsistently across platforms.
    if (
        vendor === "intel" ||
        /\b(?:intel|uhd|iris|xe|adreno|mali|llvmpipe|swiftshader)\b/.test(
            haystack,
        )
    ) {
        return "integrated";
    }

    // Apple on a non-M architecture (or vendor "apple" without architecture)
    // — classify integrated by default. Macs without Apple Silicon are
    // increasingly rare; if a user IS on an Intel Mac with a real discrete
    // AMD chip the AMD branch above already caught it.
    if (vendor === "apple") {
        return "integrated";
    }

    return "unknown";
}

/**
 * Subset of `GPUAdapterInfo` we read. Declared locally so this file doesn't
 * depend on a `@webgpu/types` install — the runtime values are plain
 * lowercase strings that match the spec.
 */
type GpuAdapterInfoLike = {
    vendor?: string;
    architecture?: string;
    device?: string;
    description?: string;
};

// ---------------------------------------------------------------------------
// getAvailableTiers — GPU-aware tier filter
// ---------------------------------------------------------------------------

/**
 * Returns the set of model tiers that should be exposed in the picker for a
 * given WebGPU detection result. Thresholds are deliberately conservative —
 * we'd rather hide a borderline tier than offer it and have model load fail
 * with a device-lost error mid-download.
 *
 *  - `supported: false`            → empty set (picker hidden entirely).
 *  - `supported: true`             → `{tiny, small}` is the floor whenever
 *                                    the toggle is visible (the 1 GiB
 *                                    buffer check in {@link detectWebGpu}
 *                                    already gates this).
 *  - `gpuClass in {discrete, apple-silicon}` → `+ medium + large`
 *                                    regardless of `estimatedVramMb`. A
 *                                    real discrete GPU has gigabytes of
 *                                    VRAM that the browser-reported
 *                                    buffer limit doesn't surface.
 *  - `estimatedVramMb >= 4096`     → `+ medium` (fallback for `unknown`
 *                                    gpuClass adapters that DO report a
 *                                    larger buffer limit — rare, but the
 *                                    old code path still works).
 *  - `estimatedVramMb >= 8192`     → `+ large` (same fallback path).
 *
 * Why the two-signal design? Chrome / Edge clamp `maxBufferSize` at ~2 GiB
 * for all GPUs, so `estimatedVramMb` is stuck at 2048 even on a 32 GB
 * RTX 5090. Without `gpuClass`, the 4096 / 8192 thresholds would never
 * trigger and high-end GPUs would only ever see Tiny+Small. See
 * {@link deriveGpuClass} for how `gpuClass` is derived from
 * `adapter.info.vendor` + `adapter.info.architecture` (vendor-based, so a
 * future NVIDIA generation needs zero changes).
 */
export function getAvailableTiers(detection: WebGpuDetection): Set<Tier> {
    if (!detection.supported) {
        // No working WebGPU adapter — caller hides the offline toggle entirely,
        // and the picker should render nothing either.
        return new Set<Tier>();
    }
    // Floor: tiny + small always show when the toggle is visible. The
    // detectWebGpu() 1 GiB buffer-size gate has already filtered out
    // adapters that couldn't run even the Small tier reliably.
    const tiers = new Set<Tier>(["tiny", "small"]);
    const vram = detection.estimatedVramMb;
    const gpuClass = detection.gpuClass;

    // Primary signal: a real discrete GPU (NVIDIA / AMD) or Apple Silicon
    // unified-memory design. These can always run Medium and ~always run
    // Large consumer-class models; the browser-reported buffer limit clamps
    // at ~2 GiB regardless of physical VRAM, so we can't gate on that here.
    if (gpuClass === "discrete" || gpuClass === "apple-silicon") {
        tiers.add("medium");
        tiers.add("large");
        return tiers;
    }

    // Secondary signal: an `'unknown'` adapter (older browser without
    // adapter.info) that happens to report a higher buffer limit. Rare in
    // practice — Chrome caps it at 2 GiB — but the fallback keeps the door
    // open for browsers that surface real VRAM numbers in the future.
    if (typeof vram === "number" && vram >= 4096) {
        tiers.add("medium");
    }
    if (typeof vram === "number" && vram >= 8192) {
        tiers.add("large");
    }
    return tiers;
}

// ---------------------------------------------------------------------------
// loadModel
// ---------------------------------------------------------------------------

// At most ONE engine is cached per tab: construction compiles WebGPU shaders,
// but pinning a second model's engine wastes the VRAM the active one needs.
type LoadedEngine = InBrowserEngine & { readonly _worker: Worker };

let cachedEngine: LoadedEngine | null = null;

/**
 * A WebGPU device that was lost or disposed never recovers, but the engine
 * holding it stays cached and keeps reporting "ready" — so every later
 * generation repeats the same error. Evicting makes the next attempt reload.
 */
const DISPOSED_DEVICE =
    /already been disposed|device (?:is |was )?lost|destroyed/i;

/** The worker proxy rejects with `err.toString()`, not an Error — a bare
 *  string reaches the store's `instanceof Error` check as "Unknown error". */
function asError(err: unknown): Error {
    return err instanceof Error ? err : new Error(String(err));
}

function evictIfDisposed(err: unknown): void {
    if (!DISPOSED_DEVICE.test(String((err as Error)?.message ?? err))) return;
    console.warn(
        "[offline] the model engine was disposed — evicting so the next run reloads",
        err,
    );
    cachedEngine?._worker.terminate();
    cachedEngine = null;
}
let inFlight: { modelId: string; promise: Promise<InBrowserEngine> } | null =
    null;

/**
 * Lazily loads the MLC engine for the given `modelId`. The `progressCb` is
 * invoked throughout the (potentially long) first-time download — its
 * `progress` field is 0..1 and `text` is the MLC pipeline's current step.
 *
 * Concurrent callers for the SAME model share the in-flight promise: a user
 * double-clicking the toggle doesn't double-download. A request for a
 * different model invalidates the current cache (the store's
 * model-switch flow is the canonical "unload current, load new" path).
 *
 * Throws when WebGPU is unsupported, when the model fails to compile (out of
 * memory, device lost), or when the dynamic import of `@mlc-ai/web-llm`
 * itself errors. The caller surfaces the failure to the UI.
 */
export async function loadModel(
    modelId: string,
    progressCb: (p: ModelLoadProgress) => void,
): Promise<InBrowserEngine> {
    if (cachedEngine && cachedEngine.modelId === modelId) {
        progressCb({ progress: 1, text: "Model ready (cached)", phase: "load" });
        return cachedEngine;
    }

    if (inFlight && inFlight.modelId === modelId) {
        return inFlight.promise;
    }

    // Different model requested. Release the old engine's GPU resources FIRST —
    // dropping the reference alone leaves its device and weights resident, and a
    // 1B still holding VRAM while an 8B loads is how the device gets lost.
    const previous = cachedEngine;
    cachedEngine = null;
    if (previous) {
        try {
            await (
                previous._engine as { unload: () => Promise<void> }
            ).unload();
        } catch (err) {
            console.warn("[offline] unloading the previous model failed", err);
        }
        previous._worker.terminate();
    }

    const promise = (async (): Promise<LoadedEngine> => {
        // Dynamic import — keeps the ~3MB web-llm bundle off the critical path
        // for users who never toggle offline mode on.
        const webllm = await import("@mlc-ai/web-llm");
        const appConfig = webLlmAppConfig(await loadModelCatalog());

        // Never disable the cache to "fix" a miss.
        let phase: ModelLoadPhase = "download";
        try {
            const cached = await webllm.hasModelInCache(modelId, appConfig);
            if (cached) phase = "load";
            else {
                console.warn(
                    `[inBrowserLlm] model "${modelId}" NOT in Cache Storage — this load will download the full model. ` +
                        `If you expected a cache hit, your browser likely evicted the entry under storage pressure ` +
                        `(per-origin quota is best-effort for the Cache API and a multi-GB model is a fat target). ` +
                        `You can confirm by opening DevTools → Application → Cache Storage and looking for "webllm/model". ` +
                        `Workarounds: pick a smaller tier in the offline mode panel, or request persistent storage ` +
                        `via navigator.storage.persist() (TODO: surface in the UI).`,
                );
            }
        } catch (err) {
            console.warn(
                "[inBrowserLlm] hasModelInCache() failed — Cache Storage may be unavailable in this context.",
                err,
            );
        }

        // Inference runs in its own thread (@mlc-ai/web-llm README §"Dedicated
        // Web Worker"): on the main thread it competes with the page's own
        // rendering, which truncated generations and lost the GPU device.
        const worker = new Worker(
            new URL("./inBrowserLlm.worker.ts", import.meta.url),
            { type: "module" },
        );

        // A worker that dies takes its pending proxy promises with it — they
        // never settle. Race the load against the failure so it surfaces.
        const workerFailure = new Promise<never>((_, reject) => {
            worker.addEventListener("error", (event) => {
                const err = asError(event.error ?? event.message);
                console.error("[offline] the model worker failed", err);
                reject(err);
            });
            worker.addEventListener("messageerror", (event) => {
                const err = new Error(t(UiKeys.Offline.WorkerMessageFailed));
                console.error("[offline] ", err, event);
                reject(err);
            });
        });

        progressCb({ progress: 0, text: "", phase });

        // The web-llm engine takes an InitProgressCallback that fires for every
        // shard / wasm-compile step; the proxy forwards it across the boundary.
        let engine: unknown;
        try {
            engine = await Promise.race([
                webllm.CreateWebWorkerMLCEngine(worker, modelId, {
                    appConfig,
                    initProgressCallback: (report) => {
                        phase = nextLoadPhase(phase, report.text ?? "");
                        progressCb({
                            phase,
                            progress: Number.isFinite(report.progress)
                                ? report.progress
                                : 0,
                            text: report.text ?? "",
                        });
                    },
                }),
                workerFailure,
            ]);
        } catch (err) {
            worker.terminate();
            throw asError(err);
        }

        cachedEngine = {
            modelId,
            _engine: engine,
            _worker: worker,
        };
        return cachedEngine;
    })();
    inFlight = { modelId, promise };

    try {
        return await promise;
    } finally {
        // Always clear the in-flight slot for this load. If a fresh load races
        // in for a different model while this one was settling, the assignment
        // above already replaced inFlight, so only clear if it still matches.
        if (inFlight && inFlight.modelId === modelId) {
            inFlight = null;
        }
    }
}

/**
 * Test-only hook: clears the in-tab engine cache so a subsequent
 * `loadModel()` call re-runs the dynamic import. Not exported through the
 * module's public surface; vitest reaches it via the module import.
 */
export function __resetInBrowserLlmForTests(): void {
    cachedEngine = null;
    inFlight = null;
}

// ---------------------------------------------------------------------------
// generatePracticeContent
// ---------------------------------------------------------------------------

/**
 * Both offline branches ask for PLAIN TEXT, never a JSON envelope or inline
 * cue markers: 1B-3B models hallucinate tags, stall in ellipsis loops and
 * break envelopes. Read-aloud gets a job it can do (write a story) and cue
 * scheduling moves out of the prompt entirely — the backend
 * `GET /api/practice/{practice}/cues` endpoint supplies the cue plan. Riff's
 * output is a single word + "I need help!" nudges, split on periods.
 *
 * The `system` half is the contract and tone; a speaker only ever supplies
 * the topic the `user` half is about.
 */

/**
 * Builds the system prompt for a given practice. The scaffold text is authored
 * in `backend/domain/Models/Localization/PromptsOffline.json` and reaches here
 * through the generated `PromptsOfflineKeys` / `PromptsOfflineValues`
 * projection — the branching stays in code, only the copy lives in the
 * resource (wiki/feature-resource-generation.md §Prompts). That resource is
 * separate from the server-side `Prompts.json` by audience: these run on a
 * 1B-3B in-browser model and must stay markedly simpler than the server
 * prompts, which never reach the browser.
 *
 * The system half carries FORM only. The length lives in the user message,
 * stated once: a 1B model cannot count what it has written, so repeating the
 * figure only buys a longer brief to drift from.
 */
export function systemPromptFor(practice: OfflinePractice): string {
    if (practice === "riff") {
        // The format example is the strongest signal a ~1B model sees — a fixed
        // example word is a standing instruction to return that word. Draw it
        // fresh per request, away from the words just served, so the example
        // steers generation instead of anchoring it.
        return t(PromptsOfflineKeys.Riff, { exampleWord: pickRiffPoolWord() });
    }

    // Archetype Play wants a story with clear EMOTIONAL TURNS so the speaker has
    // natural moments to shift archetype against — but the *which* / *when* of
    // the shifts is NOT in the prose (small models can't weave inline cue
    // markers, and the offline path schedules shifts mechanically via the
    // backend cue endpoint + the user's chosen sequence).
    const archetypeClause =
        practice === "archetype"
            ? `${t(PromptsOfflineKeys.ArchetypeClause)} `
            : "";

    return t(PromptsOfflineKeys.ReadAloud, { archetypeClause });
}

const DEFAULT_TOPICS: Record<Exclude<OfflinePractice, "riff">, string> = {
    instrument: PromptsOfflineKeys.TopicInstrument,
    "body-language": PromptsOfflineKeys.TopicBodyLanguage,
    archetype: PromptsOfflineKeys.TopicArchetype,
};

/** The topic a story is about when the speaker gives none; Riff has none. */
export function defaultTopic(practice: OfflinePractice): string {
    return practice === "riff" ? "" : t(DEFAULT_TOPICS[practice]);
}

/**
 * Draws a Riff word category by its catalog weight. The draw is in code because
 * a 1B model cannot honour a percentage.
 */
export function drawRiffWordCategory(
    random: () => number = Math.random,
): (typeof RIFF_WORD_CATEGORIES)[number] {
    const total = RIFF_WORD_CATEGORIES.reduce((sum, c) => sum + c.weight, 0);
    let roll = random() * total;
    for (const category of RIFF_WORD_CATEGORIES) {
        roll -= category.weight;
        if (roll < 0) return category;
    }
    return RIFF_WORD_CATEGORIES[RIFF_WORD_CATEGORIES.length - 1];
}

/**
 * The user message. A custom `topic` only fills the topic slot: the length
 * (read-aloud) or the word-and-cue-count format (Riff) is always the app's,
 * so no typed text can turn a sized story into something else. A Riff theme
 * replaces the category draw.
 */
export function userPromptFor(
    practice: OfflinePractice,
    targetWords: number,
    topic: string,
    cueCount: number,
    random: () => number = Math.random,
): string {
    const custom = topic.trim().replace(/[\s.!?]+$/, "");
    if (practice === "riff") {
        const wordClause = custom
            ? t(PromptsOfflineKeys.RiffTheme, { topic: custom })
            : t(PromptsOfflineKeys.RiffCategory, {
                  category: drawRiffWordCategory(random).description,
              });
        return t(PromptsOfflineKeys.RiffRequest, {
            cueCount,
            wordClause: `${wordClause} `,
        });
    }
    return custom
        ? t(PromptsOfflineKeys.CustomStoryRequest, { targetWords, topic: custom })
        : t(PromptsOfflineKeys.StoryRequest, {
              targetWords,
              topic: defaultTopic(practice),
          });
}

// ---------------------------------------------------------------------------
// Minimal web-llm engine shape, typed locally so a test mock doesn't have to
// satisfy the full SDK surface. Both overloads are accepted.
// ---------------------------------------------------------------------------
type WebLlmUsage = { completion_tokens?: number | null };
type WebLlmChunk = {
    choices: {
        delta?: { content?: string | null };
        finish_reason?: string | null;
    }[];
    usage?: WebLlmUsage | null;
};
type WebLlmCompletion = {
    choices: {
        message: { content?: string | null };
        finish_reason?: string | null;
    }[];
    usage?: WebLlmUsage | null;
};
type WebLlmCreateRequest = {
    messages: { role: "system" | "user" | "assistant"; content: string }[];
    temperature?: number;
    repetition_penalty?: number;
    max_tokens?: number;
    response_format?: { type: "json_object" };
    stream?: boolean;
    stream_options?: { include_usage: boolean };
};
type WebLlmEngine = {
    chat: {
        completions: {
            // Overload-friendly union: streaming requests return an async iterable,
            // non-streaming return the completed message. The runtime checks
            // `Symbol.asyncIterator` to discriminate; tests can return either shape.
            create: (
                req: WebLlmCreateRequest,
            ) => Promise<AsyncIterable<WebLlmChunk> | WebLlmCompletion>;
        };
    };
    // Fire-and-forget on the worker proxy: it posts `interruptGenerate` and drops
    // the reply promise, so the engine only sees the flag on its NEXT decode step
    // and the stream must still be drained to its end.
    interruptGenerate?: () => void;
};

/**
 * `tokens` is a whitespace-fragment estimate, not a real BPE count — a UI
 * counter, never billing. Feed (tokens, at) into a {@link RollingTokRate}:
 * web-llm batches 1–8 tokens per microtask, so the raw arrival rate is jagged.
 */
export type TokenProgress = {
    tokens: number;
    at: number;
};

/**
 * One completion's text plus what the model itself reported about it.
 * `completionTokens` is the real count from `stream_options.include_usage`
 * (the whitespace estimate is only the live counter's stand-in until it
 * arrives); `finishReason` is `'length'` when our `max_tokens` ceiling cut the
 * piece off and `'stop'` when the model ended on its own. Both are absent when
 * the engine omits them.
 */
export type StreamedCompletion = {
    text: string;
    completionTokens?: number;
    finishReason?: string;
    // Set when WE cut the generation off at the overrun ceiling. A third outcome:
    // `finishReason` then carries the engine's own `abort`, not `stop`/`length`.
    interrupted?: boolean;
};

/**
 * Hands {@link collectStreamedContent} the only two things it needs to cut a
 * runaway generation short: where the ceiling is, and how to signal the engine.
 */
export type StreamOverrunGuard = {
    maxWords: number;
    interrupt: () => void;
};

/**
 * Optional knobs for {@link generatePracticeContent}.
 *
 * - `topic`: the speaker's own topic for this take; blank means the
 *   practice's default. See {@link userPromptFor}.
 *
 * - `onTokenProgress`: per-chunk streaming callback for the tok/s UI. When
 *   undefined the engine call falls back to the non-streaming overload —
 *   the response arrives as one completed message and the UI gets no
 *   per-token motion. The practice pages always set this; tests sometimes
 *   skip it to assert the non-streaming path.
 */
export type GeneratePracticeContentOptions = {
    topic?: string;
    onTokenProgress?: (p: TokenProgress) => void;
    /**
     * Target word count for the body — the whole of how duration and rate
     * reach the model. Defaults to {@link DEFAULT_PRACTICE_LENGTH_MINUTES} at
     * the speaker's own {@link wordsPerMinute} when omitted.
     */
    targetWords?: number;
    /** Riff's "I need help!" cue count (`riffCueCountFor`); required for Riff, ignored otherwise. */
    cueCount?: number;
    /**
     * Title callback — fires ONCE with the story title. Body-first: the
     * read-aloud title is GENERATED from the finished story via a short
     * post-stream completion, so it fires AFTER the body has streamed. The page
     * shows a playful placeholder in the title slot while the body streams, then
     * cross-fades to this title. On an empty / failed title completion a curated
     * playful fallback title is emitted instead. Never fires for Riff (its
     * "title" is the constant `'Riff'`; the page renders the single word, not a
     * title header). Optional — callers that don't need the title-arrival signal
     * can read the final `content.title` instead.
     */
    onTitle?: (title: string) => void;
    /**
     * Cumulative body text per chunk. For read-aloud the accumulator IS the
     * body, so this and {@link onRawBodyProgress} are fed identically; both
     * names exist so either consumer can pass either. Never fires for Riff.
     */
    onBodyProgress?: (partialBody: string) => void;
    /** @see onBodyProgress — same accumulator, separate slot. */
    onRawBodyProgress?: (partialRawBody: string) => void;
};

/**
 * Share of the promised word count a read-aloud piece must reach — loose
 * enough for a small model's normal undershoot, tight enough to catch the
 * 10× miss that leaves a five-minute take with a thirty-second script.
 */
export const MIN_TARGET_WORD_RATIO = 0.5;

/**
 * Times a too-short read-aloud piece is continued before it fails. Measured on
 * Llama-3.2-1B: first passes land at 52-79% of the target, so one pass usually
 * clears the gate and the second only catches a stubborn undershoot.
 */
export const CONTINUATION_PASSES = 2;

/**
 * Share of the promised word count past which a read-aloud piece is running
 * away rather than landing — the model has had its overshoot and is now
 * costing GPU time for words nobody asked for. 20% leaves room for a model
 * that is genuinely about to finish.
 */
export const MAX_TARGET_WORD_RATIO = 1.2;

/**
 * Share of a read-aloud piece's sentences/lines that must be distinct. A word
 * count alone is satisfied by the garbage it should reject — two hundred
 * repeats of a four-word line clear a 600-word target.
 */
export const MIN_DISTINCT_SEGMENT_RATIO = 0.5;

/**
 * Share of a read-aloud piece that must be closed-class English words. A piece
 * can be long enough and all-distinct and still be unreadable: a stream of
 * proper nouns with no "the", no "and", no verb in place scores near 1 on both
 * the other gates. Measured: this model's own prose 0.41-0.43, hand-written
 * narrative 0.55, the repo's densest technical prose 0.26, the salad 0.00.
 */
export const MIN_FUNCTION_WORD_RATIO = 0.2;

/**
 * Token budget per promised word — the SERVER's allowance, generated, so the
 * two generation paths cannot disagree about it.
 *
 * Prose alone is ~1.35 tokens/word, but a read-aloud body also carries inline
 * `[[Label:Direction | message]]` markers whose brackets, colon and pipe are
 * each their own token and whose label never reaches the word count. At the cue
 * density the prompt asks for, a legitimate piece can exceed twice its word
 * count in tokens — which is how a local guess of 2 could truncate a CORRECT
 * generation.
 */
export const MAX_TOKENS_PER_WORD = OutputTokensPerWord;

/**
 * Every model in our catalogue has a 4096-token context window, and the window
 * holds the PROMPT AND THE GENERATION together — so a ceiling above it asks for
 * something the device cannot produce, however generous the per-word allowance.
 * The server's allowance is sized for a provider whose thinking is unpredictable
 * and billed from the same pool; nothing here thinks, so the budget is the
 * window minus room for the prompt.
 */
export const MODEL_CONTEXT_TOKENS = 4096;
export const PROMPT_TOKEN_HEADROOM = 1024;

/** Closed-class English. Not localized copy: the practice content is English. */
const FUNCTION_WORDS = new Set(
    `a an the and or but so because if then than that this these those there here
   of in on at to for from by with without into onto over under about as
   is are was were be been being am do does did done have has had having
   i you he she it we they me him her us them my your his its our their
   not no nor too very just only also even still yet up out off down again
   what which who whom whose when where why how
   will would can could shall should may might must`.split(/\s+/),
);

export function countWords(text: string): number {
    return text
        .trim()
        .split(/\s+/)
        .filter((w) => w.length > 0).length;
}

/**
 * Cuts a body back to its last sentence terminator so an interrupted story
 * never ends mid-clause. A body with no terminator at all is returned whole —
 * a ragged ending beats handing back nothing.
 */
export function trimToLastSentence(text: string): string {
    const end = Math.max(
        text.lastIndexOf("."),
        text.lastIndexOf("!"),
        text.lastIndexOf("?"),
    );
    return end < 0 ? text : text.slice(0, end + 1);
}

/** Distinct share of the piece's sentences and lines; 1 when there are none. */
export function distinctSegmentRatio(text: string): number {
    const segments = text
        .split(/[.!?\n]+/)
        .map((s) =>
            s
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, " ")
                .trim(),
        )
        .filter((s) => s.length > 0);
    return segments.length === 0 ? 1 : new Set(segments).size / segments.length;
}

/** Closed-class share of the piece's words; 1 when there are none. */
export function functionWordRatio(text: string): number {
    const words = text.toLowerCase().match(/[a-z']+/g) ?? [];
    if (words.length === 0) return 1;
    return words.filter((w) => FUNCTION_WORDS.has(w)).length / words.length;
}

/**
 * Runs the practice-content prompt on the given engine and projects the
 * response onto a `PracticeContentEnvelope` identical to what the backend
 * AG-UI `practice_content` event emits — so the Instrument / Body Language
 * pages can render either source uniformly.
 *
 * `sessionId` is set to a synthetic `offline:<random>` value so downstream
 * de-duplication keys (e.g. RecordingPanel's blob key) remain unique per take.
 * `practice` matches the backend `Practice` enum literal — "TheInstrument"
 * for Instrument and "BodyLanguagePlay" for Body Language — so any code
 * that switches on `content.practice` works unchanged in offline mode.
 *
 * On a malformed Riff output (no salvageable JSON) we fall back to a
 * deterministic word + empty cue list so the page can still mount. A
 * read-aloud piece looping one line or not reading as English is retried once;
 * one far short of `targetWords` is continued up to {@link CONTINUATION_PASSES}
 * times. Either still failing is THROWN — silently accepted, it is a wasted take.
 *
 * The third parameter accepts either an options object or a bare
 * `onTokenProgress` callback; the options-object form is the canonical one.
 */
export async function generatePracticeContent(
    practice: OfflinePractice,
    engine: InBrowserEngine,
    optionsOrCallback?:
        | GeneratePracticeContentOptions
        | ((p: TokenProgress) => void),
): Promise<PracticeContentEnvelope> {
    // Parameter normalisation — accept either a plain callback or an options
    // object; a "function" value is treated as the bare `onTokenProgress`.
    const options: GeneratePracticeContentOptions =
        typeof optionsOrCallback === "function"
            ? { onTokenProgress: optionsOrCallback }
            : (optionsOrCallback ?? {});

    const llm = engine._engine as WebLlmEngine;
    const targetWords =
        typeof options.targetWords === "number" && options.targetWords > 0
            ? options.targetWords
            : targetWordsFor(DEFAULT_PRACTICE_LENGTH_MINUTES, wordsPerMinute());

    const cueCount = options.cueCount ?? 0;
    if (practice === "riff" && cueCount < 1) {
        throw new Error("An offline Riff needs its cue count.");
    }

    const systemPrompt = systemPromptFor(practice);

    const userPrompt = userPromptFor(
        practice,
        targetWords,
        options.topic ?? "",
        cueCount,
    );

    // We never pass `response_format: { type: 'json_object' }` — neither branch
    // produces JSON, and its grammar-constrained sampler buffers the whole
    // output internally, stalling the streaming tok/s meter at "0.0".
    const streaming = options.onTokenProgress !== undefined;
    // Read-aloud only: Riff's reply is a word plus a handful of cue sentences,
    // far under any ceiling worth guarding.
    const overrunWords = Math.ceil(targetWords * MAX_TARGET_WORD_RATIO);
    // Continuation passes add to this count, so the live counter keeps rising
    // instead of dropping back to zero mid-story.
    let tokensShown = 0;
    // A non-empty `story` makes this a continuation: the story so far rides as
    // the assistant turn and the stream previews as story + the new text.
    const runCompletion = async (
        system: string,
        story = "",
    ): Promise<StreamedCompletion> => {
        const storyWords = countWords(story);
        const tokenBase = story ? tokensShown : 0;
        const onTokenProgress =
            options.onTokenProgress &&
            ((p: TokenProgress) => {
                tokensShown = tokenBase + p.tokens;
                options.onTokenProgress?.({ ...p, tokens: tokensShown });
            });
        const preview = (onProgress?: (partial: string) => void) =>
            practice === "riff" || !onProgress
                ? undefined
                : story
                  ? (partial: string) =>
                        onProgress(
                            joinStory(story, dropLeadingEcho(story, partial)),
                        )
                  : onProgress;
        try {
            const response = await llm.chat.completions.create({
                messages: [
                    { role: "system", content: system },
                    { role: "user", content: userPrompt },
                    ...(story
                        ? [
                              { role: "assistant" as const, content: story },
                              {
                                  role: "user" as const,
                                  content: t(PromptsOfflineKeys.Continue, {
                                      missingWords: targetWords - storyWords,
                                  }),
                              },
                          ]
                        : []),
                ],
                temperature: 0.85,
                // The ONLY penalty. web-llm's kernel subtracts `count * frequency_penalty`
                // from a token's logit, so over 600 words it drives "the" (~50 repeats)
                // past every rival and the model can only pick words it has never used —
                // which is exactly the proper-noun salad it produced. `repetition_penalty`
                // is count-independent: one appearance costs the same as fifty, so it
                // cannot starve English however long the piece runs.
                repetition_penalty: 1.1,
                // web-llm treats an absent `max_tokens` as Infinity, so the bound is the
                // only thing that stops a model that will not stop itself.
                max_tokens: Math.min(
                    Math.ceil((targetWords - storyWords) * MAX_TOKENS_PER_WORD),
                    MODEL_CONTEXT_TOKENS - PROMPT_TOKEN_HEADROOM,
                ),
                stream: streaming,
                // The model's own token count and finish reason ride the final
                // chunk — the only way to tell a ceiling cut from a clean stop.
                ...(streaming
                    ? { stream_options: { include_usage: true } }
                    : {}),
            });

            const completion = await collectStreamedContent(
                response,
                onTokenProgress,
                preview(options.onBodyProgress),
                preview(options.onRawBodyProgress),
                practice === "riff"
                    ? undefined
                    : {
                          // An echoed story spends this budget too, yet a story
                          // under half the target still ends past half of it.
                          maxWords: overrunWords - storyWords,
                          interrupt: () => llm.interruptGenerate?.(),
                      },
            );
            if (completion.interrupted) {
                console.warn(
                    `[offline] read-aloud generation interrupted: kept ${countWords(completion.text)} words ` +
                        `after passing the ${overrunWords}-word ceiling (${MAX_TARGET_WORD_RATIO}× a ${targetWords}-word target)`,
                );
            }
            return completion;
        } catch (err) {
            evictIfDisposed(err);
            throw asError(err);
        }
    };

    // Riff branches to its own envelope shape — `body` carries the single
    // word and `_riffCues` carries the pre-generated "I need help!" list.
    // We keep the PracticeContentEnvelope (one consistent return type) so
    // existing rendering paths (e.g. RecordingPanel's blob key) still get a
    // stable `sessionId` + `practice` + `body` triple.
    if (practice === "riff") {
        // A fresh press must hand the speaker a word they have not just seen. The
        // small answer space makes a 1B model repeat itself, so we re-roll — each
        // retry redraws the prompt's format example (the strongest steer a 1B
        // model reads) away from the recent words. A banned-word list in the
        // prompt is deliberately NOT the lever: it grows without bound and puts
        // the very words we want avoided into context.
        let word = "";
        let cues: string[] = [];
        for (let attempt = 0; attempt < RIFF_REROLL_ATTEMPTS; attempt += 1) {
            // Plain-text contract: the model emits the word first, then N help
            // sentences, each ending in a single period. Split on '.', trim, drop
            // empties — segment [0] is the word, the rest are the cues.
            const segments = (
                await runCompletion(
                    attempt === 0 ? systemPrompt : systemPromptFor("riff"),
                )
            ).text
                .split(".")
                .map((s) => s.trim())
                .filter((s) => s.length > 0);
            const candidate = extractRiffWord(segments[0]);
            cues = extractRiffCues(segments.slice(1), cueCount);
            if (!isRecentRiffWord(candidate)) {
                word = candidate;
                break;
            }
        }
        // A stubborn model never yields — the pool decides, and it excludes the
        // recent words too, so the press still changes the word.
        if (word === "") word = pickRiffPoolWord();
        rememberRiffWord(word);

        const riffEnvelope: PracticeContentEnvelope & { _riffCues?: string[] } =
            {
                sessionId: synthesiseOfflineSessionId(),
                practice: Practice.Riff,
                // Set contentKind=Story so existing renderers that switch on it
                // (Poem vs Story) keep a sensible default. The page treats Riff
                // specially anyway via `getRiffCues`.
                contentKind: "Story",
                title: "Riff",
                body: word,
            };
        if (cues.length > 0) {
            riffEnvelope._riffCues = cues;
        }
        return riffEnvelope;
    }

    // Read-aloud — the accumulator IS the body. Clean off any leading
    // "Here's a story:" preamble the model snuck in despite the instruction
    // (cheap defensive prefix strip; the brief permits a soft pass since
    // small models can't be relied on to follow every clause).
    const tooShort = (text: string) =>
        countWords(text) < targetWords * MIN_TARGET_WORD_RATIO;
    const looping = (text: string) =>
        distinctSegmentRatio(text) < MIN_DISTINCT_SEGMENT_RATIO;
    const incoherent = (text: string) =>
        functionWordRatio(text) < MIN_FUNCTION_WORD_RATIO;

    // At most 1 + 1 fresh retry + CONTINUATION_PASSES story completions: a
    // broken piece is regenerated, a merely short one is continued.
    let completion = await runCompletion(systemPrompt);
    let body = cleanReadAloudProse(completion.text);
    if (looping(body) || incoherent(body)) {
        completion = await runCompletion(systemPrompt);
        body = cleanReadAloudProse(completion.text);
    }
    let completionTokens = completion.completionTokens;
    for (
        let pass = 1;
        pass <= CONTINUATION_PASSES &&
        tooShort(body) &&
        !looping(body) &&
        !incoherent(body);
        pass += 1
    ) {
        completion = await runCompletion(systemPrompt, body);
        body = joinStory(
            body,
            dropLeadingEcho(body, cleanReadAloudProse(completion.text)),
        );
        completionTokens =
            completionTokens === undefined ||
            completion.completionTokens === undefined
                ? undefined
                : completionTokens + completion.completionTokens;
    }
    const words = countWords(body);
    // Three endings, never conflated: `stop` the model finished, `length` our
    // max_tokens ceiling truncated it, `interrupted_overrun` we cut it off.
    const outcome = `finish_reason=${completion.interrupted ? "interrupted_overrun" : (completion.finishReason ?? "unknown")} completion_tokens=${completionTokens ?? "unknown"} max_tokens=${Math.ceil(targetWords * MAX_TOKENS_PER_WORD)}`;
    if (tooShort(body)) {
        console.warn(
            `[offline] read-aloud piece too short: ${words} words for a ${targetWords}-word target (${outcome})`,
            body,
        );
        throw new Error(
            t(UiKeys.Offline.TooShort, { Words: words, Target: targetWords }),
        );
    }
    if (looping(body)) {
        const percent = Math.round(distinctSegmentRatio(body) * 100);
        console.warn(
            `[offline] read-aloud piece is looping: ${percent}% of its ${words} words are distinct lines (${outcome})`,
            body,
        );
        throw new Error(t(UiKeys.Offline.Repetitive, { Percent: percent }));
    }
    if (incoherent(body)) {
        const percent = Math.round(functionWordRatio(body) * 100);
        console.warn(
            `[offline] read-aloud piece is not English prose: ${percent}% of its ${words} words are everyday connecting words (${outcome})`,
            body,
        );
        throw new Error(t(UiKeys.Offline.Incoherent, { Percent: percent }));
    }

    // Body-first title: once the story body is finished, run ONE short focused
    // completion against the warm engine to title it. A single-output task small
    // models do reliably. On empty / error we fall back to a random curated
    // playful title. Surface the result via `onTitle` so the page can cross-fade
    // it in over the playful placeholder.
    const title = await generateOfflineTitle(llm, body);
    options.onTitle?.(title);

    return {
        sessionId: synthesiseOfflineSessionId(),
        practice: offlinePracticeLiteral(practice),
        contentKind: "Story",
        title,
        body,
        completionTokens,
    };
}

/**
 * Drops the continuation's leading sentences that the story already holds — a
 * small model often echoes the story back before adding to it. Run on every
 * streamed partial too, so an echo in progress never flashes into the preview.
 */
export function dropLeadingEcho(story: string, continuation: string): string {
    const norm = (s: string) =>
        s
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, " ")
            .trim();
    const told = norm(story);
    const segments = continuation.match(/[^.!?\n]+[.!?\n]*\s*/g) ?? [];
    let echoed = 0;
    while (echoed < segments.length && told.includes(norm(segments[echoed])))
        echoed += 1;
    return segments.slice(echoed).join("").trim();
}

function joinStory(story: string, continuation: string): string {
    return continuation ? `${story}\n\n${continuation}` : story;
}

/**
 * Generates a short, playful title for a finished offline story via a single
 * focused completion against the already-warm engine. Sanitises the result
 * (strips quotes / a "Title:" prefix / trailing punctuation, collapses
 * whitespace, clamps to ~8 words). On an empty or failed completion, falls
 * back to a random curated playful title — NEVER the old single static label.
 */
async function generateOfflineTitle(
    llm: WebLlmEngine,
    body: string,
): Promise<string> {
    try {
        const response = await llm.chat.completions.create({
            messages: [
                {
                    role: "user",
                    content:
                        "Give this story a short, playful title — 2 to 6 words. Reply with " +
                        "ONLY the title, no quotes, no punctuation at the end.\n\nStory:\n" +
                        body,
                },
            ],
            temperature: 0.8,
            max_tokens: 32,
            stream: false,
        });
        const rawTitle = (
            response as { choices?: Array<{ message?: { content?: unknown } }> }
        ).choices?.[0]?.message?.content;
        const sanitised =
            typeof rawTitle === "string" ? sanitiseOfflineTitle(rawTitle) : "";
        return sanitised.length > 0 ? sanitised : randomFallbackTitle();
    } catch {
        // Engine error mid-title (e.g. context overflow) — never block the story
        // on the title; fall back to a curated playful title.
        return randomFallbackTitle();
    }
}

/**
 * Cleans a raw title completion into a presentable title: strips a leading
 * "Title:" label, surrounding quotes, trailing punctuation, collapses
 * whitespace, and clamps to ~8 words. Returns '' when nothing usable remains
 * (the caller substitutes a curated fallback).
 */
export function sanitiseOfflineTitle(raw: string): string {
    let text = raw.trim();
    if (text.length === 0) return "";

    // Take only the first line — small models sometimes ramble past the title.
    const firstLine = text.split("\n").find((l) => l.trim().length > 0);
    text = (firstLine ?? "").trim();

    // Strip a leading "Title:" / "Story title -" style label.
    text = text.replace(/^\s*(?:the\s+)?title\s*[:\-–—]\s*/i, "").trim();

    // Strip surrounding matched quotes (straight or smart) and asterisks/backticks.
    text = text
        .replace(/^["'“”‘’*`]+/, "")
        .replace(/["'“”‘’*`]+$/, "")
        .trim();

    // Strip trailing sentence punctuation (but keep "…" off entirely — a title
    // shouldn't trail off; placeholders own the ellipsis).
    text = text.replace(/[.,;:!?…]+$/u, "").trim();

    // Collapse internal whitespace.
    text = text.replace(/\s+/g, " ").trim();

    // Clamp to ~8 words so a runaway completion can't produce a paragraph-title.
    const words = text.split(" ").filter((w) => w.length > 0);
    if (words.length > 8) {
        text = words.slice(0, 8).join(" ");
    }

    return text;
}

/**
 * Maps an offline read-aloud practice key onto its backend `Practice`
 * enum literal — the value surfaced on the returned `PracticeContentEnvelope`.
 * Riff is handled on its own branch above and never reaches
 * here.
 */
function offlinePracticeLiteral(
    practice: OfflinePractice,
): PracticeContentEnvelope["practice"] {
    switch (practice) {
        case "instrument":
            return Practice.TheInstrument;
        case "archetype":
            return Practice.ArchetypePlay;
        case "body-language":
        default:
            return Practice.BodyLanguagePlay;
    }
}

/**
 * Strips common preamble patterns ("Here is a story:", "Sure! Here you go:")
 * and surrounding whitespace / markdown fences from a prose response.
 * Tolerant of well-behaved output (no-op when the model already gave just
 * prose); a cheap defensive filter for the rare small-model lapse.
 */
function cleanReadAloudProse(raw: string): string {
    if (!raw) return "";
    let text = raw.trim();

    // Strip a markdown code fence if the model wrapped the prose in one.
    if (text.startsWith("```")) {
        const end = text.lastIndexOf("```");
        if (end > 3) {
            text = text.slice(3, end);
            // Drop the optional language tag on the opening fence line.
            const firstNewline = text.indexOf("\n");
            if (firstNewline >= 0 && firstNewline < 20) {
                text = text.slice(firstNewline + 1);
            }
            text = text.trim();
        }
    }

    // Strip a leading "Here is a story:" / "Sure! Here you go:" preamble —
    // the model sometimes emits one short line of acknowledgement before the
    // story despite the "no preamble" instruction. We only strip when the
    // first paragraph is short (< 80 chars) AND ends with ':' or '.', so we
    // never accidentally lop off a real first sentence.
    const preambleMatch = text.match(/^(.{1,80}?[:.])\s*\n+/);
    if (preambleMatch) {
        const head = preambleMatch[1].toLowerCase();
        if (
            head.startsWith("here") ||
            head.startsWith("sure") ||
            head.startsWith("of course") ||
            head.startsWith("certainly") ||
            head.includes("story") ||
            head.includes("practice")
        ) {
            text = text.slice(preambleMatch[0].length).trim();
        }
    }

    return text;
}

/**
 * Reads the Riff "I need help!" cue list off a Riff `PracticeContentEnvelope`.
 * The page calls this once at session start and reveals one cue
 * per click of the "I need help!" button. Returns an empty array when the
 * envelope is not from Riff or when the AI returned no cues (e.g. malformed
 * JSON without any salvageable items — the page falls back to a single
 * encouragement line).
 */
export function getRiffCues(
    content: PracticeContentEnvelope,
): readonly string[] {
    const carrier = content as PracticeContentEnvelope & {
        _riffCues?: string[];
    };
    return Array.isArray(carrier._riffCues) ? carrier._riffCues : [];
}

/**
 * Degenerate echoes a small in-browser model parrots back from the prompt
 * instead of inventing a real word. When the parsed `word` is one of these,
 * we treat it as invalid and fall back to a curated pool — otherwise the
 * Riff page renders "Help" on almost every offline run.
 */
const RIFF_DEGENERATE_WORDS = new Set(["help", "word", "cue", "cues", "riff"]);

/**
 * A small pool of words, drawn from at both ends of the Riff flow: it supplies
 * the format example in the system prompt, and the fallback word when the
 * model's own answer is unusable. Rotated (rather than always returning the
 * same one) so neither end anchors on a single word. Its make-up roughly
 * follows `RIFF_WORD_CATEGORIES`: mostly everyday words, a few from the rest.
 *
 * `lighthouse` is deliberately absent. It is the mode-collapse seed the server
 * prompts' anti-cliché clause names, and it sat here at position one AND in the
 * prompt's example — anchoring the word at both the generation and the recovery
 * end.
 */
const RIFF_WORD_POOL = [
    "kitchen",
    "rain",
    "bicycle",
    "promise",
    "window",
    "breakfast",
    "thunder",
    "garden",
    "suitcase",
    "hunger",
    "neighbour",
    "birthday",
    "deadline",
    "budget",
    "customer",
    "meeting",
    "password",
    "smartphone",
    "pilgrimage",
    "verdict",
] as const;

let riffPoolCounter = 0;

/**
 * The words this tab has already served, most recent last. Session-only
 * module state (same shape as {@link cachedEngine}) — a reload starts clean;
 * the requirement is only that two presses in a row differ.
 *
 * Bounded so the exclusion can never starve {@link RIFF_WORD_POOL}.
 */
const recentRiffWords: string[] = [];
const RIFF_RECENT_LIMIT = 5;

/** Attempts at a non-repeating word from the model before the pool decides. */
const RIFF_REROLL_ATTEMPTS = 3;

function isRecentRiffWord(word: string): boolean {
    return recentRiffWords.includes(word);
}

function rememberRiffWord(word: string): void {
    recentRiffWords.push(word);
    if (recentRiffWords.length > RIFF_RECENT_LIMIT) recentRiffWords.shift();
}

/**
 * Draws a pool word the speaker has not just seen — so both the prompt's
 * format example (which steers the model away from the last few words) and
 * the unusable-reply fallback stay fresh.
 */
function pickRiffPoolWord(): string {
    const candidates = RIFF_WORD_POOL.filter((w) => !isRecentRiffWord(w));
    const pool = candidates.length > 0 ? candidates : RIFF_WORD_POOL;
    // Prefer real randomness when available (this module already uses
    // Math.random elsewhere); fall back to a rotating counter otherwise.
    const idx =
        typeof Math.random === "function"
            ? Math.floor(Math.random() * pool.length)
            : riffPoolCounter % pool.length;
    riffPoolCounter += 1;
    return pool[idx];
}

/**
 * Reduces the first period-split segment of a Riff plain-text reply to the
 * single English word. Takes the first alphabetic token (in case the model
 * disobeyed and wrote several words before its first period), lowercases and
 * strips it to `a-z`, then applies the validity rules (length 2-30, not a
 * degenerate prompt echo like "help"/"word"/…). Falls back to a varied
 * curated pool on anything unusable. Exported for unit testing.
 */
export function extractRiffWord(segment: string | undefined): string {
    if (typeof segment === "string") {
        // The model may have emitted several whitespace-separated tokens before
        // its first period — take the first alphabetic-bearing token.
        const token = segment
            .trim()
            .split(/\s+/)
            .find((t) => /[a-z]/i.test(t));
        const cleaned = (token ?? "").toLowerCase().replace(/[^a-z]/g, "");
        if (
            cleaned.length >= 2 &&
            cleaned.length <= 30 &&
            !RIFF_DEGENERATE_WORDS.has(cleaned)
        ) {
            return cleaned;
        }
    }
    // Fallback word — picked so the Riff page always has something to render
    // even when the model returns gibberish or echoes a prompt term.
    return pickRiffPoolWord();
}

/**
 * Filters the already-split, already-trimmed cue segments of a Riff
 * plain-text reply into the "I need help!" list. Drops empties and
 * over-long entries (the <200 char guard), then clamps the list size to
 * `cueCount`. Returns an empty array when nothing salvageable remains
 * so the page can pick a fallback. Exported for unit testing.
 */
export function extractRiffCues(
    segments: string[],
    cueCount: number,
): string[] {
    if (!Array.isArray(segments)) return [];
    const cleaned: string[] = [];
    for (const s of segments) {
        if (typeof s === "string") {
            const trimmed = s.trim();
            if (trimmed.length > 0 && trimmed.length < 200) {
                cleaned.push(trimmed);
            }
        }
    }
    return cleaned.slice(0, cueCount);
}

/**
 * Drains either a streaming `AsyncIterable<WebLlmChunk>` or a completed
 * `WebLlmCompletion` into the full assistant string. When a token-progress
 * callback is supplied AND the response is iterable, each chunk's
 * `delta.content` is appended and the running token count is fired. The
 * "token" count is a whitespace-split word-fragment estimate — close enough
 * to BPE tokens at ~1.0× for English prose, and orders of magnitude cheaper
 * than calling `engine.tokenizer.encode(...)` on every chunk.
 *
 * If the response is the non-streaming shape (test mocks, or an
 * onTokenProgress=undefined caller) we short-circuit to the
 * `choices[0].message.content` field and never fire the progress callback.
 *
 * Exported for unit testing.
 */
export async function collectStreamedContent(
    response: AsyncIterable<WebLlmChunk> | WebLlmCompletion,
    onTokenProgress?: (p: TokenProgress) => void,
    onBodyProgress?: (partialBody: string) => void,
    onRawBodyProgress?: (partialRawBody: string) => void,
    overrunGuard?: StreamOverrunGuard,
): Promise<StreamedCompletion> {
    // The streaming overload returns an object with Symbol.asyncIterator; the
    // completed-message overload returns a plain object with `choices`. We
    // discriminate on the iterator protocol rather than a `stream:true` flag
    // so test mocks can return either shape without faking SDK plumbing.
    const isStream =
        response !== null &&
        typeof response === "object" &&
        Symbol.asyncIterator in (response as object);

    if (!isStream) {
        const completion = response as WebLlmCompletion;
        return {
            text: completion?.choices?.[0]?.message?.content ?? "",
            completionTokens: completion?.usage?.completion_tokens ?? undefined,
            finishReason: completion?.choices?.[0]?.finish_reason ?? undefined,
        };
    }

    let acc = "";
    let tokens = 0;
    let completionTokens: number | undefined;
    let finishReason: string | undefined;
    let interrupted = false;
    const now = () =>
        typeof performance !== "undefined" &&
        typeof performance.now === "function"
            ? performance.now()
            : Date.now();

    for await (const chunk of response as AsyncIterable<WebLlmChunk>) {
        // `stream_options.include_usage` puts the real counts on a final
        // usage-only chunk; `finish_reason` says whether the ceiling cut it.
        completionTokens = chunk?.usage?.completion_tokens ?? completionTokens;
        finishReason = chunk?.choices?.[0]?.finish_reason ?? finishReason;
        const delta = chunk?.choices?.[0]?.delta?.content;
        if (typeof delta === "string" && delta.length > 0 && !interrupted) {
            acc += delta;
            // Cheap token estimate: each non-empty whitespace-delimited fragment is
            // ~1 BPE token. Punctuation-glued tokens get under-counted; that's fine
            // — the counter is for UI smoothness, not billing.
            const fragmentTokens = delta
                .split(/\s+/)
                .filter((s) => s.length > 0).length;
            // Minimum one increment per non-empty chunk so the counter still ticks
            // when the model emits a single bare-token shard like " " or "}".
            tokens += Math.max(1, fragmentTokens);
            onTokenProgress?.({ tokens, at: now() });

            // For the read-aloud branch the accumulator IS the body — no JSON
            // envelope to peel away, no inline `<cue>` markers to strip. Forward
            // the live accumulator to both callbacks; the consumer (typically
            // StreamingStoryView via the `rawBody` prop) gets continuous motion.
            onBodyProgress?.(acc);
            onRawBodyProgress?.(acc);

            if (overrunGuard && countWords(acc) > overrunGuard.maxWords) {
                interrupted = true;
                overrunGuard.interrupt();
                acc = trimToLastSentence(acc);
                onBodyProgress?.(acc);
                onRawBodyProgress?.(acc);
            }
        }
    }

    // The loop above keeps pulling after an interrupt on purpose: the worker's
    // generator only decodes when asked, and it releases the engine's per-model
    // lock at its END — abandoning it would deadlock every later generation.
    // The interrupt flag caps that tail at the engine's final + usage chunks.
    return { text: acc, completionTokens, finishReason, interrupted };
}

/**
 * Rolling tokens-per-second meter, used by the UI to render a smooth tok/s
 * number while {@link generatePracticeContent} is streaming. Chunk arrivals
 * are bursty (web-llm flushes 1–8 tokens per microtask), so a naive
 * `tokens / elapsed` reading flickers. The window keeps the last ~500 ms of
 * `(tokens, at)` samples and computes the rate over that span — a low-pass
 * filter in the time domain.
 *
 * The output is still updated on every sample (the caller throttles to
 * requestAnimationFrame or a CSS transition for the visual ease-out), but the
 * underlying number changes only gradually because old samples drop out of
 * the window slowly.
 *
 * Usage:
 *   const meter = new RollingTokRate(500);
 *   onTokenProgress: (p) => uiTokRate = meter.push(p.tokens, p.at);
 */
export class RollingTokRate {
    private samples: { tokens: number; at: number }[] = [];
    private readonly windowMs: number;

    constructor(windowMs: number = 500) {
        this.windowMs = windowMs;
    }

    /**
     * Records a new sample and returns the rate (tokens/sec) over the trailing
     * `windowMs` milliseconds. Returns 0 when the window contains <2 samples
     * or spans <50ms — both cases produce wild estimates that look like a
     * spike to the user.
     */
    push(tokens: number, at: number): number {
        this.samples.push({ tokens, at });
        // Trim anything older than `at - windowMs`. We always keep the most
        // recent sample even if the window briefly contains exactly one entry.
        const cutoff = at - this.windowMs;
        while (this.samples.length > 1 && this.samples[0].at < cutoff) {
            this.samples.shift();
        }
        if (this.samples.length < 2) return 0;
        const first = this.samples[0];
        const last = this.samples[this.samples.length - 1];
        const elapsedMs = last.at - first.at;
        if (elapsedMs < 50) return 0;
        const tokenDelta = last.tokens - first.tokens;
        return (tokenDelta / elapsedMs) * 1000;
    }

    reset(): void {
        this.samples = [];
    }
}

/**
 * Synthesises a unique-per-take session id under the `offline:` prefix so it
 * cannot collide with a backend UUID. Used as the synthetic `sessionId` on
 * the offline-generated `PracticeContentEnvelope`.
 */
function synthesiseOfflineSessionId(): string {
    // `crypto.randomUUID` is available in every WebGPU-capable browser (a
    // strict subset of crypto.subtle support), so the SSR fallback only
    // matters for unit tests.
    if (
        typeof crypto !== "undefined" &&
        typeof crypto.randomUUID === "function"
    ) {
        return `offline:${crypto.randomUUID()}`;
    }
    return `offline:${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}
